import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/brand/StatusBadge";
import AppointmentDialog from "@/components/appointments/AppointmentDialog";
import ConsultantAgenda from "@/components/appointments/ConsultantAgenda";
import { APPT_TYPES, APPT_STATUS, statusChip, statusTone } from "@/lib/appointments";
import { CalendarDays, Loader2, ChevronLeft, ChevronRight, Plus, Clock, Search, MapPin } from "lucide-react";

const DOW = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export default function AppointmentsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canAgenda = user?.role === "admin" || user?.role === "backoffice";
  const navigate = useNavigate();
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offices, setOffices] = useState([]);
  const [operators, setOperators] = useState([]);
  const [officeId, setOfficeId] = useState("");
  const [operatorId, setOperatorId] = useState("");
  const [typeF, setTypeF] = useState("");
  const [statusF, setStatusF] = useState("");
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null);

  useEffect(() => {
    api.get("/offices").then(({ data }) => setOffices(data)).catch(() => {});
    if (isAdmin) api.get("/users").then(({ data }) => setOperators(data.filter((u) => u.active))).catch(() => {});
  }, [isAdmin]);

  const range = useMemo(() => {
    if (view === "day") return [anchor, anchor];
    if (view === "week") { const s = startOfWeek(anchor); return [s, addDays(s, 6)]; }
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return [startOfWeek(first), addDays(startOfWeek(last), 6)];
  }, [view, anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: iso(range[0]), date_to: iso(range[1]) };
      if (officeId) params.office_id = officeId;
      if (operatorId) params.operator_id = operatorId;
      if (typeF) params.type = typeF;
      if (statusF) params.status = statusF;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/appointments", { params });
      setItems(data);
    } catch (e) { setItems([]); } finally { setLoading(false); }
  }, [range, officeId, operatorId, typeF, statusF, q]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);

  const byDay = useMemo(() => {
    const m = {};
    items.forEach((a) => { (m[a.appt_date] = m[a.appt_date] || []).push(a); });
    Object.values(m).forEach((arr) => arr.sort((x, y) => (x.appt_time || "").localeCompare(y.appt_time || "")));
    return m;
  }, [items]);

  const move = (dir) => {
    if (view === "day") setAnchor(addDays(anchor, dir));
    else if (view === "week") setAnchor(addDays(anchor, dir * 7));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));
  };
  const jump = (which) => {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    if (which === "today") { setView("day"); setAnchor(d); }
    else if (which === "tomorrow") { setView("day"); setAnchor(addDays(d, 1)); }
    else if (which === "week") { setView("week"); setAnchor(d); }
    else { setView("month"); setAnchor(d); }
  };

  const title = view === "month" ? `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`
    : view === "day" ? anchor.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })
    : `${range[0].toLocaleDateString("it-IT", { day: "numeric", month: "short" })} – ${range[1].toLocaleDateString("it-IT", { day: "numeric", month: "short" })}`;

  const Chip = ({ a }) => (
    <button onClick={() => setDlg({ appointment: a })} data-testid={`appt-chip-${a.id}`}
      title={`${a.customer_name} · ${a.status}`}
      className={`block w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] font-medium hover:opacity-80 ${statusChip(a.status)}`}>
      {a.appt_time ? `${a.appt_time} ` : ""}{a.customer_name}
    </button>
  );

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i));
  const monthDays = Array.from({ length: Math.round((range[1] - range[0]) / 86400000) + 1 }, (_, i) => addDays(range[0], i));

  return (
    <div className="animate-fade-in-up space-y-5" data-testid="appointments-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="ds-page-title flex items-center gap-2"><CalendarDays className="h-6 w-6 text-brand" /> Appuntamenti</h1>
          <p className="mt-1 text-sm text-slate-500">Agenda giornaliera, settimanale e mensile collegata a clienti, opportunità e forniture.</p>
        </div>
        {user?.can_manage_appointments && <Button onClick={() => setDlg({ defaultDate: iso(anchor) })} className="gap-1.5" data-testid="new-appointment-button"><Plus className="h-4 w-4" /> Nuovo appuntamento</Button>}
      </div>

      <div className="ds-card space-y-3 p-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => move(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" onClick={() => { const d = new Date(); d.setHours(0,0,0,0); setAnchor(d); }} data-testid="cal-today">Oggi</Button>
            <Button size="sm" variant="outline" onClick={() => move(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
            <span className="ml-2 font-display font-semibold capitalize text-slate-800">{title}</span>
          </div>
          <div className="flex rounded-lg border border-border p-0.5">
            {["day", "week", "month"].map((v) => (
              <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`}
                className={`rounded-md px-3 py-1 text-sm font-medium ${view === v ? "bg-brand text-white" : "text-slate-600"}`}>
                {v === "day" ? "Giorno" : v === "week" ? "Settimana" : "Mese"}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
          <div className="flex gap-1">
            {[["today", "Oggi"], ["tomorrow", "Domani"], ["week", "Settimana"], ["month", "Mese"]].map(([k, lbl]) => (
              <button key={k} onClick={() => jump(k)} data-testid={`quick-${k}`} className="rounded-full border border-border px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-brand hover:text-brand">{lbl}</button>
            ))}
          </div>
          <div className="relative"><Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input className="h-9 w-[190px] pl-8 text-sm" placeholder="Cerca cliente / telefono…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="cal-search" />
          </div>
          <Select value={typeF || "all"} onValueChange={(v) => setTypeF(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-[140px]" data-testid="cal-type"><SelectValue placeholder="Tipologia" /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">Tutte le tipologie</SelectItem>{APPT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={statusF || "all"} onValueChange={(v) => setStatusF(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-[140px]" data-testid="cal-status"><SelectValue placeholder="Stato" /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">Tutti gli stati</SelectItem>{APPT_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          {isAdmin && (
            <>
              <Select value={officeId || "all"} onValueChange={(v) => setOfficeId(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9 w-[140px]" data-testid="cal-office"><SelectValue placeholder="Sede" /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="all">Tutte le sedi</SelectItem>{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={operatorId || "all"} onValueChange={(v) => setOperatorId(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9 w-[150px]" data-testid="cal-operator"><SelectValue placeholder="Consulente" /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="all">Tutti</SelectItem>{operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </>
          )}
        </div>
      </div>

      {canAgenda && <ConsultantAgenda defaultDate={iso(anchor)} />}

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : view === "day" ? (
        <div className="ds-card p-4" data-testid="agenda-day">
          {(byDay[iso(anchor)] || []).length === 0 ? <div className="py-10 text-center text-sm text-slate-500">Nessun appuntamento in questa giornata.</div> : (
            <div className="space-y-2">{(byDay[iso(anchor)] || []).map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-lg border border-border p-3" data-testid={`appt-row-${a.id}`}>
                <div><div className="flex flex-wrap items-center gap-2 font-semibold text-slate-800">
                  {a.appt_time && <span className="inline-flex items-center gap-1 text-brand"><Clock className="h-4 w-4" />{a.appt_time}{a.end_time ? `–${a.end_time}` : ""}</span>} {a.customer_name}
                  <StatusBadge type={statusTone(a.status)}>{a.status}</StatusBadge>
                </div>
                  <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-slate-500">
                    <span>{a.type}</span>
                    {a.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{a.location}</span>}
                    {a.consultant_name && <span>{a.consultant_name}</span>}
                    {a.opportunity_product && <span>Opp: {a.opportunity_product}</span>}
                  </div></div>
                <Button size="sm" variant="outline" onClick={() => setDlg({ appointment: a })} data-testid={`open-appt-${a.id}`}>Apri</Button>
              </div>))}
            </div>
          )}
        </div>
      ) : view === "week" ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-7" data-testid="agenda-week">
          {weekDays.map((d) => (
            <div key={iso(d)} className="ds-card min-h-[140px] p-2">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-500">{DOW[(d.getDay() + 6) % 7]} {d.getDate()}</span>
                <button onClick={() => setDlg({ defaultDate: iso(d) })} className="text-slate-400 hover:text-brand"><Plus className="h-3.5 w-3.5" /></button>
              </div>
              <div className="space-y-1">{(byDay[iso(d)] || []).map((a) => <Chip key={a.id} a={a} />)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="ds-card overflow-hidden" data-testid="agenda-month">
          <div className="grid grid-cols-7 border-b border-border bg-slate-50 text-center text-xs font-semibold text-slate-500">
            {DOW.map((d) => <div key={d} className="py-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {monthDays.map((d) => {
              const inMonth = d.getMonth() === anchor.getMonth();
              const dayItems = byDay[iso(d)] || [];
              return (
                <div key={iso(d)} className={`min-h-[92px] border-b border-r border-slate-100 p-1 ${inMonth ? "" : "bg-slate-50/60"}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${inMonth ? "text-slate-700" : "text-slate-300"}`}>{d.getDate()}</span>
                    <button onClick={() => setDlg({ defaultDate: iso(d) })} className="text-slate-300 hover:text-brand"><Plus className="h-3 w-3" /></button>
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {dayItems.slice(0, 2).map((a) => <Chip key={a.id} a={a} />)}
                    {dayItems.length > 2 && <button onClick={() => { setView("day"); setAnchor(new Date(d)); }} className="text-[10px] text-slate-400">+{dayItems.length - 2} altri</button>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {dlg && <AppointmentDialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)} appointment={dlg.appointment} defaultDate={dlg.defaultDate} onSaved={() => { setDlg(null); load(); }} />}
    </div>
  );
}
